// src/services/FileManager/fileManager.service.ts
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import Notification from '../../shared/components/Notification/index';
import { NotificationTypes } from '../../enums/notificationTypes';
import axiosInstance from '../../interceptor/axiosInstance';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { fileServiceMessages } from './file.service.constants';
import { FileMapResponse } from '../../types/FileManagerTypes';

export interface FileViewLinkResponse {
  url: string;
  fileName: string;
  expiresIn: number;
  expiresAt: string;
}

const FileManagerService = () => {
  const getFileMapping = (enabled = true): UseQueryOptions<FileMapResponse, Error> => ({
    queryKey: [QueryKeys.GET_FOLDERS_FOR_SELECTION],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get<FileMapResponse>(ApiRoutes.FILEMANAGER_GET_FILE_MAPPING, { params: { isTrash: 'false' } });
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.GET_FILE_MAPPING_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled,
  });

  const getViewLink = (fileId?: string, enabled = true): UseQueryOptions<FileViewLinkResponse, Error> => ({
    queryKey: [QueryKeys.GET_FILE_VIEW_LINK, fileId],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get<FileViewLinkResponse>(
          ApiRoutes.FILEMANAGER_PREVIEW_URL.replace(':id', encodeURIComponent(fileId as string)),
        );
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PREVIEW.ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.PREVIEW.ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: enabled && !!fileId,
  });

  // Resolves the Dataset backing an already-uploaded file set, so "Add from
  // Data Portal" can reuse its PostGIS table instead of re-ingesting the file
  // from S3 into a new, independent one (which would drop any fields added
  // directly to a layer's live table since the original upload). Failure is
  // non-fatal to the caller — it just falls back to a fresh ingest — so this
  // deliberately has no error Notification.
  const resolveDatasetForFiles = (): UseMutationOptions<{ datasetId: string | null }, Error, { folderId: string; fileNames: string[] }> => ({
    mutationKey: [MutationKeys.RESOLVE_DATASET_FOR_FILES],
    mutationFn: async ({ folderId, fileNames }) => {
      const response = await axiosInstance.get<{ datasetId: string | null }>(ApiRoutes.FILEMANAGER_DATASET_FOR_FILES, {
        params: { folderId, fileNames },
      });
      return response.data;
    },
  });

  return { getFileMapping, getViewLink, resolveDatasetForFiles };
};

export default FileManagerService;
